import React, { useState, useEffect } from 'react';
import { apiClient } from '../services/api';
import './IntakeFormDocumentUpload.css';

interface UploadedDocument {
  id: string;
  original_file_name: string;
  file_size: number;
  mime_type: string;
  uploaded_at: string;
  document_type?: string;
  transferred_to_case_id?: string;
  transferred_document_id?: string;
  transferred_at?: string;
  is_transferred?: boolean;
}

interface IntakeFormDocumentUploadProps {
  formSessionId: string;
  onChange?: (documents: UploadedDocument[]) => void;
  required?: boolean;
  maxFiles?: number;
  acceptedTypes?: string[];
  uploaderEmail?: string;
}

const IntakeFormDocumentUpload: React.FC<IntakeFormDocumentUploadProps> = ({
  formSessionId,
  onChange,
  required = false,
  maxFiles = 10,
  acceptedTypes = ['.pdf', '.jpg', '.jpeg', '.png', '.doc', '.docx'],
  uploaderEmail = 'client@example.com'
}) => {
  const [documents, setDocuments] = useState<UploadedDocument[]>([]);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [dragOver, setDragOver] = useState(false);
  const [selectedDocumentType, setSelectedDocumentType] = useState<string>('other');

  useEffect(() => {
    if (formSessionId) {
      loadExistingDocuments();
    }
  }, [formSessionId]);

  const loadExistingDocuments = async () => {
    try {
      const response = await apiClient.getIntakeFormDocuments(formSessionId);
      setDocuments(response.documents || []);
      if (onChange) {
        onChange(response.documents || []);
      }
    } catch (error) {
      console.error('Error loading existing documents:', error);
    }
  };

  const handleFileSelect = async (files: FileList) => {
    if (!formSessionId) {
      setError('Form session not initialized');
      return;
    }

    if (documents.length + files.length > maxFiles) {
      setError(`Maximum ${maxFiles} files allowed`);
      return;
    }

    setUploading(true);
    setError(null);

    try {
      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        
        // Validate file type
        const fileExtension = '.' + file.name.split('.').pop()?.toLowerCase();
        if (!acceptedTypes.includes(fileExtension)) {
          throw new Error(`File type ${fileExtension} not supported. Accepted types: ${acceptedTypes.join(', ')}`);
        }

        // Validate file size (10MB limit)
        if (file.size > 10 * 1024 * 1024) {
          throw new Error(`File ${file.name} is too large. Maximum size is 10MB.`);
        }

        const formData = new FormData();
        formData.append('document', file);
        formData.append('documentType', selectedDocumentType);
        formData.append('uploaderEmail', uploaderEmail);

        const response = await apiClient.uploadIntakeFormDocument(formSessionId, formData);
        
        // Add the new document to the list
        const newDocument: UploadedDocument = {
          id: response.document.id,
          original_file_name: response.document.original_file_name,
          file_size: response.document.file_size,
          mime_type: response.document.mime_type,
          uploaded_at: response.document.uploaded_at,
          document_type: response.document.document_type
        };

        setDocuments(prev => {
          const updated = [...prev, newDocument];
          if (onChange) {
            onChange(updated);
          }
          return updated;
        });
      }
    } catch (error: any) {
      console.error('Error uploading documents:', error);
      setError(error.message || 'Failed to upload documents');
    } finally {
      setUploading(false);
    }
  };

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      handleFileSelect(e.target.files);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      handleFileSelect(e.dataTransfer.files);
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
  };

  const handleDeleteDocument = async (documentId: string) => {
    try {
      await apiClient.deleteIntakeFormDocument(documentId);
      
      setDocuments(prev => {
        const updated = prev.filter(doc => doc.id !== documentId);
        if (onChange) {
          onChange(updated);
        }
        return updated;
      });
    } catch (error) {
      console.error('Error deleting document:', error);
      setError('Failed to delete document');
    }
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const formatDate = (dateString: string): string => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getDocumentTypeLabel = (documentType?: string): string => {
    const typeMap: { [key: string]: string } = {
      'passport': 'Passport',
      'birth_certificate': 'Birth Certificate',
      'marriage_certificate': 'Marriage Certificate',
      'employment_letter': 'Employment Letter',
      'bank_statements': 'Bank Statements',
      'tax_returns': 'Tax Returns',
      'medical_examination': 'Medical Examination',
      'police_clearance': 'Police Clearance',
      'educational_credentials': 'Educational Credentials',
      'other': 'Other'
    };
    return typeMap[documentType || 'other'] || 'Other';
  };

  // Document type options based on database schema
  const documentTypeOptions = [
    { value: 'passport', label: 'Passport' },
    { value: 'birth_certificate', label: 'Birth Certificate' },
    { value: 'marriage_certificate', label: 'Marriage Certificate' },
    { value: 'employment_letter', label: 'Employment Letter' },
    { value: 'bank_statements', label: 'Bank Statements' },
    { value: 'tax_returns', label: 'Tax Returns' },
    { value: 'medical_examination', label: 'Medical Examination' },
    { value: 'police_clearance', label: 'Police Clearance' },
    { value: 'educational_credentials', label: 'Educational Credentials' },
    { value: 'other', label: 'Other' }
  ];

  return (
    <div className="intake-document-upload">
      <div className="upload-header">
        <h4>Document Upload</h4>
        {required && <span className="required-indicator">*</span>}
        <p className="upload-instructions">
          Upload supporting documents for your application. Accepted formats: {acceptedTypes.join(', ')}
        </p>
      </div>

      {/* Document Type Selection */}
      <div className="document-type-selection">
        <label htmlFor="document-type" className="document-type-label">
          Document Type:
        </label>
        <select
          id="document-type"
          value={selectedDocumentType}
          onChange={(e) => setSelectedDocumentType(e.target.value)}
          className="document-type-select"
        >
          {documentTypeOptions.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
        <p className="document-type-help">
          Select the type of document you're uploading to help us categorize it properly.
        </p>
      </div>

      {error && (
        <div className="upload-error">
          <span className="error-icon">⚠️</span>
          {error}
        </div>
      )}

      <div 
        className={`upload-dropzone ${dragOver ? 'drag-over' : ''} ${uploading ? 'uploading' : ''}`}
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
      >
        {uploading ? (
          <div className="upload-progress">
            <div className="spinner"></div>
            <p>Uploading documents...</p>
          </div>
        ) : (
          <>
            <div className="upload-icon">📁</div>
            <p className="upload-text">
              Drag and drop files here, or{' '}
              <label className="upload-link">
                browse files
                <input
                  type="file"
                  multiple
                  accept={acceptedTypes.join(',')}
                  onChange={handleFileInputChange}
                  style={{ display: 'none' }}
                />
              </label>
            </p>
            <p className="upload-limits">
              Maximum {maxFiles} files, 10MB each
            </p>
          </>
        )}
      </div>

      {documents.length > 0 && (
        <div className="uploaded-documents">
          <h5>Uploaded Documents ({documents.length})</h5>
          <div className="document-list">
            {documents.map((doc) => (
              <div key={doc.id} className={`document-item ${doc.is_transferred ? 'transferred' : ''}`}>
                <div className="document-info">
                  <div className="document-name">
                    {doc.original_file_name || 'Unknown file'}
                    <span className="document-type-tag" title={`Document type: ${getDocumentTypeLabel(doc.document_type)}`}>
                      📄 {getDocumentTypeLabel(doc.document_type)}
                    </span>
                    {doc.is_transferred && (
                      <span className="transfer-status" title="Document transferred to case">
                        ✅ Transferred
                      </span>
                    )}
                  </div>
                  <div className="document-meta">
                    {formatFileSize(doc.file_size || 0)} • {formatDate(doc.uploaded_at)}
                    {doc.is_transferred && doc.transferred_at && (
                      <span className="transfer-date">
                        • Transferred {formatDate(doc.transferred_at)}
                      </span>
                    )}
                  </div>
                </div>
                {!doc.is_transferred && (
                  <button
                    type="button"
                    className="delete-document-btn"
                    onClick={() => handleDeleteDocument(doc.id)}
                    title="Delete document"
                  >
                    🗑️
                  </button>
                )}
                {doc.is_transferred && (
                  <div className="transferred-indicator" title="Document has been transferred to your case">
                    📁
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {required && documents.length === 0 && (
        <div className="validation-message">
          At least one document is required
        </div>
      )}
    </div>
  );
};

export default IntakeFormDocumentUpload;
